import React, {
  memo, useCallback, useRef, useEffect, useState, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import Form, { FormConsumer } from '@active/react-ui/Form';
import { isEmail } from '@active/core/email/validate';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import OrderReceiptAndAccount from '../../../common/passportWidgets/components/OrderReceiptAndAccount';
import { useCheckEmailExistMutation } from '../../../../store/services/checkout/checkoutApi';
import { useLogTrackMutation } from '../../../../store/services/common/logTrack/logTrackApi';
import pubSubInstance from '../../../../shared/utils/pubSub';
import { isCryptogram } from '../../../../shared/utils/commonUtils';

const isValidateCryptogram = (cryptogram, accountEmail) => {
  return cryptogram && cryptogram !== accountEmail && isCryptogram(cryptogram);
};

const OrderReceiptContainer = () => {
  const orderReceiptAndAccountTrigger = useRef(null);
  const formRef = useRef(null);

  const [ checkEmailExist ] = useCheckEmailExistMutation();
  const [ logTrack ] = useLogTrackMutation();
  const [ isReceiptAccountCreated, setIsReceiptAccountCreated ] = useState(false);
  const [ isExistEmail, setIsExistEmail ] = useState(false);
  const [ errorMsgId, setErrorMsgId ] = useState(undefined);

  const {
    orderReceiptAndAccountParams = {},
    locale,
    widgetNameSpace,
  } = useSelector(state => state.propsData);

  const {
    entities: {
      userInfo = {},
    },
    defaultData,
  } = useSelector(state => state.mainStateData);

  const {
    isReceiptAccountEmailReadOnly,
    isShowCryptogram: isShowCryptogramProp,
    receiptAccountEmail: receiptAccountEmailProp,
    countryCode,
    isShowReceiptAndAccountSection,
    firstName, lastName,
  } = orderReceiptAndAccountParams;

  const defaultFormFields = !isEmpty(defaultData) ? pick(defaultData, [
    'receiptAccountEmail',
    'receiptAccountFirstName',
    'receiptAccountLastName',
  ]) : {
    receiptAccountEmail: isShowReceiptAndAccountSection ? receiptAccountEmailProp : undefined,
    receiptAccountFirstName: isShowReceiptAndAccountSection ? firstName : undefined,
    receiptAccountLastName: isShowReceiptAndAccountSection ? lastName : undefined,
  };

  const {
    epid,
    contactInfo: { email } = {},
    passportInfo,
  } = userInfo;

  const orderReceiptAndAccountTriggers = {
    createAccountTrigger: (trigger) => {
      orderReceiptAndAccountTrigger.current = trigger;
    },
  };

  const isShowCryptogram = useMemo(() => {
    return !isReceiptAccountCreated && !email && epid && isShowCryptogramProp;
  }, [ isReceiptAccountCreated, email, epid, isShowCryptogramProp ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      if (!formRef.current) {
        resolve(true);
        return;
      }
      if (isExistEmail) {
        resolve(false);
        return;
      }
      formRef.current.validateForm({ shouldTouchFields: true }).then((res) => {
        resolve(isEmpty(res));
      });
    });
  }, [ formRef, isExistEmail ]);

  const handleSubmit = useCallback(() => {
    setErrorMsgId(undefined);
    if (!formRef.current) {
      return Promise.resolve({ status: true });
    }
    const countryValue = countryCode ? { countryCode } : {};
    const {
      receiptAccountEmail,
      receiptAccountFirstName,
      receiptAccountLastName,
      receiptAccountCryptogram,
    } = formRef.current.values;

    const createReceiptAndAcccountParams = {
      email: receiptAccountEmail || '',
      password: receiptAccountCryptogram,
      firstName: receiptAccountFirstName || 'unknown',
      lastName: receiptAccountLastName || 'unknown',
      ...countryValue,
    };
    const partialUpdateForParams = true;
    const isCreateOrderReceiptAndAccount = isShowReceiptAndAccountSection && isShowCryptogram
    && receiptAccountCryptogram && orderReceiptAndAccountTrigger.current;
    return new Promise((resolve) => {
      isCreateOrderReceiptAndAccount ? orderReceiptAndAccountTrigger.current(({ status, message }) => {
        const createAccountStatus = (status === 'SUCCESS');
        if (createAccountStatus) {
          setIsReceiptAccountCreated(true);
          formRef.current.setFieldValue('receiptAccountCryptogram', '');
          formRef.current.setFieldValue('receiptAccountFirstName', '');
          formRef.current.setFieldValue('receiptAccountLastName', '');
          resolve({
            status: true,
            ...omit(formRef.current.values, [ 'receiptAccountCryptogram' ]),
          });
        } else {
          const errorId = receiptAccountEmail ? 'checkout.lib.components.error.createAcountFail' : 'checkout.lib.components.orderReceiptAndAccount.emailAddressRequired';
          setErrorMsgId(errorId);
          resolve({
            status: false,
          });
          logTrack?.({
            params: {
              message: `create acount failed: ${errorId} ${message}`,
            },
          });
        }
      }, createReceiptAndAcccountParams, partialUpdateForParams) : resolve({
        status: true,
        ...omit(formRef.current.values, [ 'receiptAccountCryptogram' ]),
      });
    });
  }, [ formRef.current, countryCode, isShowReceiptAndAccountSection, isShowCryptogram,
    orderReceiptAndAccountTrigger.current,
  ]);

  const handleCheckEmailExist = useCallback(() => {
    const { receiptAccountEmail, receiptAccountCryptogram } = formRef.current.values;

    if (isShowCryptogram && receiptAccountEmail && isEmail(receiptAccountEmail)
    && isValidateCryptogram(receiptAccountCryptogram, receiptAccountEmail)) {
      checkEmailExist({
        params: { username: receiptAccountEmail },
      }).unwrap().then(({ existing }) => setIsExistEmail(existing));
    } else {
      setIsExistEmail(false);
    }
  }, [ formRef.current, isShowCryptogram, checkEmailExist, setIsExistEmail ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-validate-block`, handleValidate);
    };
  }, [ handleValidate, widgetNameSpace ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-submit-block`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-submit-block`, handleSubmit);
    };
  }, [ handleSubmit, widgetNameSpace ]);

  if (!isShowReceiptAndAccountSection) {
    return null;
  }

  return (
    <Form
      className="order-receipt-and-account cui-mg-t-60"
      initialValues={defaultFormFields}
      customizeLayout>
      <OrderReceiptAndAccount
        errorMsgId={errorMsgId}
        locale={locale}
        epid={epid}
        passportInfo={passportInfo}
        {...orderReceiptAndAccountParams}
        isReceiptAccountEmailReadOnly={isReceiptAccountEmailReadOnly && receiptAccountEmailProp}
        isShowCryptogram={isShowCryptogram}
        isExistEmail={isExistEmail}
        checkEmailExist={handleCheckEmailExist}
        triggers={orderReceiptAndAccountTriggers}/>
      <FormConsumer>
        {
          ({
            form,
          }) => {
            formRef.current = form;
          }
        }
      </FormConsumer>
    </Form>);
};

export default memo(OrderReceiptContainer);
